/**
 * ImportHospitalModal - Modal for importing hospitals via Excel
 */
import React from 'react';
import { Upload, FileSpreadsheet, X as XIcon } from 'lucide-react';

interface ImportResult {
  success: number;
  created: number;
  updated: number;
  errors: string[];
}

interface ImportHospitalModalProps {
  show: boolean;
  importFile: File | null;
  importing: boolean;
  importResult: ImportResult | null;
  onFileChange: (file: File | null) => void;
  onDownloadTemplate: () => void;
  onImport: () => void;
  onClose: () => void;
}

export const ImportHospitalModal: React.FC<ImportHospitalModalProps> = ({
  show,
  importFile,
  importing,
  importResult,
  onFileChange,
  onDownloadTemplate,
  onImport,
  onClose,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Importar Hospitais via Excel
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Download Template */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
              Baixe o template Excel para preencher os dados dos hospitais:
            </p>
            <button
              onClick={onDownloadTemplate}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              <FileSpreadsheet className="w-4 h-4" /> Baixar Template
            </button>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Arquivo Excel
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
              {importFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{importFile.name}</p>
                    <p className="text-xs text-gray-500">
                      {(importFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => onFileChange(null)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Clique para selecionar ou arraste o arquivo
                  </p>
                  <p className="text-xs text-gray-400 mt-1">.xlsx ou .xls</p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Result */}
          {importResult && (
            <div className={`rounded-lg p-4 ${
              importResult.errors.length > 0
                ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            }`}>
              {importResult.success > 0 && (
                <div className="mb-2">
                  <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                    ✅ {importResult.success} hospitais importados com sucesso
                  </p>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1 ml-5 space-y-0.5">
                    {importResult.created > 0 && (
                      <p>• {importResult.created} {importResult.created === 1 ? 'novo hospital criado' : 'novos hospitais criados'}</p>
                    )}
                    {importResult.updated > 0 && (
                      <p>• {importResult.updated} {importResult.updated === 1 ? 'hospital atualizado' : 'hospitais atualizados'} (CNPJ existente)</p>
                    )}
                  </div>
                </div>
              )}
              {importResult.errors.length > 0 && (
                <div>
                  <p className="text-sm text-red-700 dark:text-red-300 mb-1">
                    ❌ Erros encontrados:
                  </p>
                  <ul className="text-xs text-red-600 dark:text-red-400 list-disc list-inside max-h-24 overflow-y-auto">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            {importResult ? 'Fechar' : 'Cancelar'}
          </button>
          {!importResult && (
            <button
              onClick={onImport}
              disabled={!importFile || importing}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 flex items-center gap-2"
            >
              {importing ? (
                <>
                  <span className="animate-spin">⏳</span> Importando...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" /> Importar
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
